/**
 * Helpers for admin-authored HTML fields (welcome page, thank-you page,
 * enrollment page). Kept free of DOM and editor imports so they can be unit
 * tested.
 */

// What minidoc's serializer keeps. Everything else it drops, unwraps or
// rewrites, so a body containing anything outside this list would lose markup
// the first time someone typed in the rich editor. Mirrors the allowlist in
// minidoc-editor's scrubber: leaf blocks carry `data-align`, links carry
// `href`, and highlight/colour carry their data attribute plus an inline style.
const ALLOWED_ATTRS: Record<string, string[]> = {
  p: ['data-align'],
  blockquote: ['data-align'],
  h1: ['data-align'],
  h2: ['data-align'],
  h3: ['data-align'],
  h4: ['data-align'],
  ul: ['data-align'],
  ol: ['data-align'],
  li: [],
  hr: [],
  a: ['href'],
  b: [],
  i: [],
  u: [],
  s: [],
  br: [],
  strong: [],
  em: [],
  mark: ['data-bg', 'style'],
  'text-color': ['data-fg', 'style'],
};

const TAG = /<(\/?)([a-z][a-z0-9-]*)((?:[^>"']|"[^"]*"|'[^']*')*)>/gi;
// Name plus its value, so a query string inside an href isn't read as an attribute.
const ATTR_NAME = /([a-z_:][a-z0-9_:.-]*)(?:\s*=\s*(?:"[^"]*"|'[^']*'|[^\s"'>]*))?/gi;

/**
 * True when the stored HTML uses anything the rich editor would strip, so the
 * field has to stay an HTML box. Fails safe: anything unrecognised counts as
 * custom HTML.
 */
export function needsHtmlSource(html?: string): boolean {
  if (!html) {
    return false;
  }
  if (html.includes('<!--')) {
    return true;
  }
  TAG.lastIndex = 0;
  let match = TAG.exec(html);
  while (match) {
    const tag = match[2].toLowerCase();
    const allowed = ALLOWED_ATTRS[tag];
    if (!allowed) {
      return true;
    }
    ATTR_NAME.lastIndex = 0;
    let attr = ATTR_NAME.exec(match[3]);
    while (attr) {
      if (!allowed.includes(attr[1].toLowerCase())) {
        return true;
      }
      attr = ATTR_NAME.exec(match[3]);
    }
    match = TAG.exec(html);
  }
  return false;
}

/**
 * The editor leaves empty blocks behind (e.g. `<p><br></p>`, `<h2></h2>`) when
 * its content is cleared, so a field can hold markup with nothing to show.
 * Callers use this before saving so an emptied field stores nothing. Custom HTML
 * is returned as-is: a layout of images and sections has no text to count.
 */
export function trimEmptyHtml(value?: string): string | undefined {
  if (!value) {
    return undefined;
  }
  if (needsHtmlSource(value)) {
    return value.trim();
  }
  const text = value.replace(/<[^>]*>/g, '').replace(/&nbsp;|\s/gi, '');
  return text ? value.trim() : undefined;
}
